/*
  Stock guide — click-to-copy Product types, and a filter over every row.

  Both exist for the same reason: a mistyped `product_type` is a SILENT orphan.
  Shopify accepts it, the product goes live, and it appears in no department and
  no menu with no warning anywhere. Copying beats retyping; finding beats
  scrolling four separate tables looking for the word "hat".

  Progressive: with JS off the page is still the whole guide, and every chip
  still shows its exact value as text.
*/
(function () {
  'use strict';

  var COPIED_MS = 1400;

  function copyText(text) {
    // The async API is tried first and its REJECTION is caught, not just its
    // absence: a permissions policy, an embedded preview or a click the browser
    // does not count as user activation all reject a clipboard that is present.
    // Measured on the draft preview, where writeText fails and execCommand works.
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).catch(function () {
        return legacyCopy(text);
      });
    }
    return legacyCopy(text);
  }

  function legacyCopy(text) {
    // The textarea has to be laid out and non-zero for the selection to take,
    // so it is parked off the viewport rather than hidden.
    return new Promise(function (resolve, reject) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;';
      document.body.appendChild(ta);
      ta.select();
      var ok = false;
      try {
        ok = document.execCommand('copy');
      } catch (e) {
        ok = false;
      }
      document.body.removeChild(ta);
      ok ? resolve() : reject(new Error('copy failed'));
    });
  }

  function init(root) {
    if (!root || root.dataset.guideReady) return;
    root.dataset.guideReady = '1';

    /* ---- copy ------------------------------------------------------- */

    var live = document.createElement('p');
    live.className = 'ef-visually-hidden';
    live.setAttribute('aria-live', 'polite');
    root.appendChild(live);

    root.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-copy]');
      if (!btn || !root.contains(btn)) return;

      var value = btn.getAttribute('data-copy');
      copyText(value).then(
        function () {
          if (btn.__t) clearTimeout(btn.__t);
          btn.setAttribute('data-copied', '');
          live.textContent = 'Copied ' + value;
          btn.__t = setTimeout(function () {
            btn.removeAttribute('data-copied');
          }, COPIED_MS);
        },
        function () {
          // Nothing to recover to — the value is already on screen as text,
          // so say so rather than failing silently.
          live.textContent = 'Could not copy. Select ' + value + ' and copy it by hand.';
        }
      );
    });

    /* ---- filter ----------------------------------------------------- */

    var input = root.querySelector('[data-guide-search]');
    var types = root.querySelector('[data-guide-types]');
    if (!input || !types) return;

    var bar = root.querySelector('[data-guide-find]');
    var count = root.querySelector('[data-guide-count]');
    var nomatch = root.querySelector('[data-guide-nomatch]');
    var clear = root.querySelector('[data-guide-clear]');
    var rows = Array.prototype.slice.call(types.querySelectorAll('[data-guide-row]'));

    // Each table wrapper is owned by the h3 (department) and, where there is
    // one, the h4 (Tops / Bottoms / …) above it. A heading whose tables have
    // all been filtered away has to go too, or the page fills with the names
    // of empty things.
    var wraps = [];
    var heads = [];
    var h3 = null;
    var h4 = null;
    Array.prototype.forEach.call(types.children, function (el) {
      if (el.classList.contains('ef-guide__h3')) {
        h3 = el;
        h4 = null;
        heads.push(el);
      } else if (el.classList.contains('ef-guide__h4')) {
        h4 = el;
        heads.push(el);
      } else if (el.classList.contains('ef-guide__tw')) {
        wraps.push({
          el: el,
          h3: h3,
          h4: h4,
          rows: Array.prototype.slice.call(el.querySelectorAll('[data-guide-row]'))
        });
      }
    });

    function show(el, visible) {
      if (!el) return;
      visible ? el.removeAttribute('data-guide-hidden') : el.setAttribute('data-guide-hidden', '');
    }

    function apply(raw) {
      var q = raw.trim().toLowerCase();
      var active = q.length > 0;

      root.toggleAttribute('data-filtering', active);
      if (bar) bar.toggleAttribute('data-empty', !active);

      if (!active) {
        rows.forEach(function (r) { show(r, true); });
        wraps.forEach(function (w) { show(w.el, true); });
        heads.forEach(function (h) { show(h, true); });
        if (nomatch) nomatch.removeAttribute('data-show');
        if (count) {
          count.textContent = '';
          count.removeAttribute('data-none');
        }
        return;
      }

      var hits = 0;
      // Headings are shown by any owned table, so they are collected across the
      // whole pass and only then written — a per-table write would let a later
      // empty table hide a heading an earlier one had filled.
      var live3 = new Set();
      var live4 = new Set();

      wraps.forEach(function (w) {
        var any = false;
        w.rows.forEach(function (r) {
          var match = (r.getAttribute('data-find') || '').indexOf(q) !== -1;
          show(r, match);
          if (match) {
            any = true;
            hits++;
          }
        });
        show(w.el, any);
        if (any) {
          if (w.h3) live3.add(w.h3);
          if (w.h4) live4.add(w.h4);
        }
      });

      // Start from every heading hidden and let the surviving tables put theirs
      // back. Walking the wraps alone leaves behind any heading that owns no
      // table at all — the lure "Step 2 / Step 3" pair — which then floats above
      // an unrelated department's results.
      heads.forEach(function (h) { show(h, live3.has(h) || live4.has(h)); });

      if (count) {
        count.textContent = hits
          ? hits + ' of ' + rows.length + (hits === 1 ? ' match' : ' matches')
          : 'No match';
        count.toggleAttribute('data-none', hits === 0);
      }
      if (nomatch) nomatch.toggleAttribute('data-show', hits === 0);
    }

    input.addEventListener('input', function () { apply(input.value); });

    input.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && input.value) {
        event.preventDefault();
        input.value = '';
        apply('');
      }
    });

    if (clear) {
      clear.addEventListener('click', function () {
        input.value = '';
        apply('');
        input.focus();
      });
    }

    // A reload can restore a typed value that the DOM has not been filtered for.
    if (input.value) apply(input.value);
  }

  function boot() {
    document.querySelectorAll('[data-ef-guide]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-ef-guide]');
    if (root) init(root);
  });
})();
